import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';

import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotifyService } from '../services/notify.service';

const GENERIC_ERROR = 'NOTIFICATIONS.MESSAGES.SOMETHING_WENT_WRONG';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {

  constructor(
    private notifyService: NotifyService,
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401) {
          this.notifyError(error);
        }
        return throwError(error);
      })
    );
  }

  private notifyError(error: HttpErrorResponse): void {
    const body = this.textBlobBody(error);

    if (body) {
      // A Blob can only be read asynchronously. The toast is a side effect, so
      // the error still propagates immediately; returning here is what keeps
      // this to exactly one toast.
      this.readText(body).then(
        text => this.notifyServerMessage(text),
        () => this.notifyService.errorMessage(GENERIC_ERROR),
      );
      return;
    }

    this.notifyService.errorMessage(this.prepareErrorMessage(error));
  }

  /**
   * A responseType: 'blob' request delivers its error body as a Blob, so the
   * usual error.error.message is undefined and every failure would otherwise
   * read as "something went wrong".
   */
  private textBlobBody(error: HttpErrorResponse): Blob | null {
    const body = error?.error;

    return body instanceof Blob && body.type?.startsWith('text/plain') ? body : null;
  }

  // FileReader rather than Blob.text(), which is not available in every
  // environment this runs in.
  private readText(body: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsText(body);
    });
  }

  private notifyServerMessage(text: string): void {
    const message = text?.trim();

    if (!message) {
      this.notifyService.errorMessage(GENERIC_ERROR);
      return;
    }

    this.notifyService.errorMessageUntranslated(message);
  }

  private prepareErrorMessage(error: HttpErrorResponse): string {
    const message = error?.error?.message;

    if (!message) {
      return GENERIC_ERROR;
    }

    if(Array.isArray(message)) {
      return  message.join('\n')
    }

    return message;
  }
}
